<template>
    <!-- Value engine: stage cards left to right; the selected stage's details below.
         Reads TimelineJSON items as-is: lineOne-Three (title), smallLineOne-Three (subtitle), description,
         imageURL, linkMenu [{label, link}], color (the stage's accent).
         editable (EditTimeline): an "add" slot after the cards, and the "editor" slot replaces the details -->
    <div class="ve">
        <div class="ve-help">
            <svg viewBox="0 0 24 24"><path d="M8 13V4.5a1.5 1.5 0 0 1 3 0V12M11 11.5v-2a1.5 1.5 0 0 1 3 0V12M14 10.5a1.5 1.5 0 0 1 3 0V12M17 11.5a1.5 1.5 0 0 1 3 0V16a6 6 0 0 1-6 6h-2 .21a6 6 0 0 1-5-2.69L7 19c-.31-.48-1.4-2.38-3.26-5.7a1.5 1.5 0 0 1 .54-2.02 1.87 1.87 0 0 1 2.28.28L8 13" /></svg>
            {{ editable ? 'Click a stage to edit it. Stages run left to right.' : 'Click a stage to see what happens there.' }}
        </div>
        <div class="ve-lane-wrap">
            <div class="ve-lane">
                <template v-for="(item, i) in items" :key="itemKey ? itemKey(item) : i">
                    <button type="button" :id="'ve-card-' + i" :class="['ve-card', { on: current === item, 've-changed': isChanged(i) }]" :style="accent(item)" :aria-pressed="current === item" @click="select(i)">
                        <span class="ve-n">{{ i + 1 }}</span>
                        <span v-if="isChanged(i)" class="ve-tag">Edited</span>
                        <span class="ve-t">
                            <template v-if="titleLines(item).length"><template v-for="(line, j) in titleLines(item)" :key="j"><br v-if="j" />{{ line }}</template></template>
                            <span v-else class="ve-empty">Untitled stage</span>
                        </span>
                        <span v-if="subLines(item).length" class="ve-s"><template v-for="(line, j) in subLines(item)" :key="j"><br v-if="j" />{{ line }}</template></span>
                        <span v-if="hasText(item.description) || item.imageURL || hasLinks(item)" class="ve-has">
                            <svg v-if="hasText(item.description)" viewBox="0 0 24 24"><title>Has details</title><path d="M4 6h16M4 12h10M4 18h14" /></svg>
                            <svg v-if="item.imageURL" viewBox="0 0 24 24"><title>Has an image</title><path d="M15 8h.01M3 6a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3zM3 16l5-5c.93-.9 2.07-.9 3 0l5 5M14 14l1-1c.93-.9 2.07-.9 3 0l3 3" /></svg>
                            <svg v-if="hasLinks(item)" viewBox="0 0 24 24"><title>Has links</title><path d="M9 15l6-6M11 6l.46-.54a5 5 0 0 1 7.07 7.07L18 13M13 18l-.4.53a5.07 5.07 0 0 1-7.12 0 4.97 4.97 0 0 1 0-7.07L6 11" /></svg>
                        </span>
                    </button>
                    <span v-if="i < items.length - 1" class="ve-arrow" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M5 12h14M15 16l4-4-4-4" /></svg></span>
                </template>
                <slot name="add" />
            </div>
        </div>

        <slot v-if="editable" name="editor" />
        <div v-else-if="current" class="ve-detail" :style="accent(current)">
            <div class="ve-dh">
                <span class="ve-n">{{ selectedIndex + 1 }}</span>
                <div class="ve-dh-t">
                    <h3>{{ titleLines(current).join(' ') || 'Untitled stage' }}</h3>
                    <div v-if="subLines(current).length" class="ve-dh-s">{{ subLines(current).join(' · ') }}</div>
                </div>
            </div>
            <div v-if="hasText(current.description)" class="ve-body" v-html="current.description"></div>
            <div v-if="current.imageURL" class="ve-img">
                <img :src="current.imageURL" :alt="'Image for ' + (titleLines(current).join(' ') || 'this stage')" @click="$emit('open-image', current.imageURL)" />
            </div>
            <div v-if="hasLinks(current)" class="ve-links">
                <a v-for="(link, j) in current.linkMenu" :key="j" class="ve-chip" :href="link.link" target="_blank" @click.prevent="$emit('open-link', link.link)">
                    <svg viewBox="0 0 24 24"><path d="M12 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6M11 13l9-9M15 4h5v5" /></svg>{{ link.label || link.link }}
                </a>
            </div>
            <p v-if="!hasText(current.description) && !current.imageURL && !hasLinks(current)" class="ve-none">No details for this stage yet.</p>
        </div>
        <p v-if="!items.length && !editable" class="ve-none">No stages yet.</p>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

export default defineComponent({
    name: 'ValueEngineView',
    // changedSteps: stage indexes that differ from the saved version (edit page Preview)
    // selected: optional v-model for the selected stage index (the editor drives it)
    // itemKey: optional stable key per stage (the editor's keys, so cards follow their stage when moved)
    props: {
        items: { type: Array, default: () => [] },
        changedSteps: { type: Array, default: null },
        editable: { type: Boolean, default: false },
        selected: { type: Number, default: null },
        itemKey: { type: Function, default: null }
    },
    // open-link: Playbook.vue opens /playbook/ links in its popup, others in a new tab
    emits: [ 'open-link', 'open-image', 'update:selected' ],
    data() {
        return {
            localSelected: 0
        }
    },
    computed: {
        selectedIndex(): number {
            const i = this.selected == null ? this.localSelected : this.selected;
            return Math.max(0, Math.min(i, this.items.length - 1));
        },
        current(): any {
            return this.items[this.selectedIndex] || null;
        }
    },
    methods: {
        select(i: number) {
            this.localSelected = i;
            this.$emit('update:selected', i);
        },
        titleLines(item: any): string[] {
            return [ item.lineOne, item.lineTwo, item.lineThree ].filter(line => line && `${line}`.trim());
        },
        subLines(item: any): string[] {
            return [ item.smallLineOne, item.smallLineTwo, item.smallLineThree ].filter(line => line && `${line}`.trim());
        },
        hasText(html: any): boolean {
            return !!html && `${html}`.replace(/<[^>]+>/g, '').trim().length > 0;
        },
        hasLinks(item: any): boolean {
            return !!(item.linkMenu && item.linkMenu.length);
        },
        isChanged(i: number): boolean {
            return !!this.changedSteps && this.changedSteps.includes(i);
        },
        accent(item: any) {
            return item.color ? { '--ve-color': item.color } : {};
        }
    }
});
</script>
